import { useState, useCallback, useRef, useEffect } from 'react';

import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';

import { useClickstreamCallback } from '@zenbusiness/application-commons-clickstream';
import Tooltip from '@zenbusiness/zui/Tooltip';

import * as Styled from './Message.style';
import { CLICKSTREAM_COMMON_PROPS } from '../utils/constants';
import { markdownComponents } from '../utils/markdownComponents';

/**
 * A component for displaying message blocks with consistent styling and optional animations
 * @param {object} props - Component props
 * @param {string} props.message - The message text to display
 * @param {string} [props.type='primary'] - The type/color of message for styling
 * @param {string} [props.size='md'] - The size of the message text
 * @param {string} [props.variant='plain'] - The styling variant
 * @param {string} [props.copyableValue] - Value to copy to clipboard when copy button is clicked
 * @param {string} [props.copyableLabel] - Label for copy button tooltip (defaults to "Copy")
 * @returns {import('react').ReactElement} The rendered Message component
 */
function Message({
  message,
  type = 'primary',
  size = 'md',
  variant = 'plain',
  copyableValue,
  copyableLabel
}) {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleCopyClick = useClickstreamCallback('linkPressed', {
    ...CLICKSTREAM_COMMON_PROPS,
    elementName: 'message_copy_button',
    elementText: copyableLabel || 'Copy'
  });

  const handleCopy = useCallback(
    async (e) => {
      e.stopPropagation();
      handleCopyClick();

      try {
        await navigator.clipboard.writeText(copyableValue);
        setCopied(true);
        timeoutRef.current = setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy value:', err);
      }
    },
    [copyableValue, handleCopyClick]
  );

  return (
    <Styled.MessageRoot variant={variant}>
      <Styled.MessageContent>
        <Styled.MessageText color={type} size={size}>
          <ReactMarkdown components={markdownComponents} rehypePlugins={[rehypeRaw]}>
            {message}
          </ReactMarkdown>
        </Styled.MessageText>
        {copyableValue && (
          <Tooltip title={copied ? 'Copied!' : copyableLabel || 'Copy'}>
            <Styled.CopyButton aria-label={copyableLabel || 'Copy'} onClick={handleCopy}>
              {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
            </Styled.CopyButton>
          </Tooltip>
        )}
      </Styled.MessageContent>
    </Styled.MessageRoot>
  );
}

Message.propTypes = {
  message: PropTypes.string.isRequired,
  type: PropTypes.oneOf(['primary', 'secondary', 'info', 'warning', 'error', 'success']),
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl', '2xl', '3xl', '4xl', '5xl']),
  variant: PropTypes.oneOf(['plain', 'card']),
  copyableValue: PropTypes.string,
  copyableLabel: PropTypes.string
};

export default Message;
